import { GetStaticPaths, GetStaticProps } from 'next';
import { isEmpty } from 'lodash';
import type { ReactElement } from 'react';
import clsx from 'clsx';

import { Article, Page, PagePaths, PaginationMeta } from '@/lib/types';
import { getAllPathsBySlug, getArticleLandingPageBySlug } from '@/lib/datocms/api';
import { getModelURL } from '@/lib/datocms/utils/get-model-url';
import { getPageUrlArray } from '@/lib/datocms/utils/get-page-url';
import { Image } from '@/components/image';
import { Layout } from '@/components/layout';
import { Link } from '@/components/link';
import { CATEGORY_SLUGS, PAGE_REVALIDATE_INTERVAL } from '@/lib/constants';
import { PageBanner } from '@/components/page-banner';
import { Pagination } from '@/components/pagination';
import { Tag } from '@/components/tag';

type Params = { category: string; number: string };
type PageProps = { articles: Article[]; page: Page; paths: PagePaths; meta: PaginationMeta };

// Static render the first page of articles listing
export const getStaticPaths: GetStaticPaths = async () => ({
  paths: Object.values(CATEGORY_SLUGS).map((category) => ({ params: { category, number: '1' } })),
  fallback: 'blocking',
});

export const getStaticProps: GetStaticProps<PageProps, Params> = async ({ params }) => {
  const { category, number = '1' } = params as Params;

  const data = await getArticleLandingPageBySlug({ categorySlug: category, page: Number(number) });

  if (!data || isEmpty(data.articles)) {
    return {
      notFound: true,
    };
  }
  const { articles, page, meta } = data;

  const pageUrlSlugs = getPageUrlArray(page);
  const paths = await getAllPathsBySlug({ slug: pageUrlSlugs });

  return {
    props: { articles, page, paths, meta },
    revalidate: PAGE_REVALIDATE_INTERVAL,
  };
};

const ArticleLandingPage = ({ articles, page, paths, meta }: PageProps): ReactElement => {
  const showPrimaryGridLayout = articles.length > 3;
  return (
    <Layout metaTags={page.metaTags} paths={paths}>
      <div className="component content col-12">
        <div className="component-content">
          <div className="row">
            <div className="component content article-listing-feature col-12">
              <div className="component-content">
                <PageBanner heroImage={page.heroImage} />
                {!isEmpty(page.parent?.title) && (
                  <h1 className="text-center field-heading text-active-blue mt-5">{page.parent?.title}</h1>
                )}
                {!isEmpty(page.title) && <div className="text-center body-1">{page.title}</div>}
              </div>
            </div>
            <div
              className={clsx('component snippet col-12 width-normal', {
                'primary-grid-layout': showPrimaryGridLayout,
              })}
            >
              <div className="component-content">
                <div className="snippet-inner">
                  <div className="snippet-container">
                    <div className="snippet">
                      <div className="row">
                        <div className="component page-list grid col-12">
                          <div className="component-content">
                            <ul className="items">
                              {articles.map((article) => (
                                <li className="item" key={article.id}>
                                  <Link href={getModelURL(article)} title={article.title}>
                                    <div className="article-wrapper">
                                      {!isEmpty(article.heroImage?.data) && (
                                        <div className="article-image">
                                          <div className="field-heroimage">
                                            <Image {...article.heroImage} isBackground />
                                          </div>
                                        </div>
                                      )}
                                      <div className="article-container">
                                        <div className="field-teasertitle">{article.title}</div>
                                        <Tag tag={article.tag} />
                                      </div>
                                    </div>
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Pagination
              currentPage={meta.currentPage}
              totalPages={meta.totalPages}
              nextPage={meta.nextPage}
              prevPage={meta.prevPage}
              basePath={getModelURL(page)}
            />
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ArticleLandingPage;
